import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from '../../config/firebase';
import { 
  collection, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

interface FormData {
  fullName: string;
  email: string;  
  mobile: string; 
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pinCode: string; 
  sanctionLoad: string;
  availableArea: string;
  electricityAccountNumber: string;
  electricityBill: File | null;
}

interface FormErrors {
  fullName: string;
  email: string;
  mobile: string;
  addressLine1: string;
  city: string;
  state: string;
  pinCode: string;
  electricityBill: string;
}

interface Status {
  type: 'success' | 'error' | null;
  message: string;
}

const Inquiry: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    mobile: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pinCode: '',
    sanctionLoad: '',
    availableArea: '',
    electricityAccountNumber: '',
    electricityBill: null
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status>({
    type: null,
    message: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const validateForm = (formData: FormData): { isValid: boolean; errors: FormErrors } => {
    const errors: FormErrors = {
      fullName: '',
      email: '',
      mobile: '',
      addressLine1: '',
      city: '',
      state: '',
      pinCode: '',
      electricityBill: '',
    };

    let isValid = true;

    // Required fields
    const requiredFields: (keyof FormData)[] = [
      'fullName',
      'email',
      'mobile',
      'addressLine1',
      'city',
      'state',
      'pinCode',
      'sanctionLoad',
      'availableArea'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        errors[field as keyof FormErrors] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`;
        isValid = false;
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Mobile validation
    if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
      isValid = false;
    }

    // Pin code validation
    if (formData.pinCode && !/^[0-9]{6}$/.test(formData.pinCode)) {
      errors.pinCode = 'Please enter a valid 6-digit pin code';
      isValid = false;
    }

    // File validation if uploaded
    if (formData.electricityBill) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

      if (formData.electricityBill.size > maxSize) {
        errors.electricityBill = 'File size must be less than 2MB';
        isValid = false;
      } else if (!allowedTypes.includes(formData.electricityBill.type)) {
        errors.electricityBill = 'Only JPG, PNG and PDF files are allowed';
        isValid = false;
      }
    }

    return { isValid, errors };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const maxSize = 2 * 1024 * 1024; // 2MB
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

      if (file.size > maxSize) {
        setStatus({ type: 'error', message: 'File size must be less than 2MB' });
        return;
      } else if (!allowedTypes.includes(file.type)) {
        setStatus({ type: 'error', message: 'Only JPG, PNG and PDF files are allowed' });
        return;
      }

      setFormData(prev => ({ ...prev, electricityBill: file }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { isValid } = validateForm(formData);

    if (!isValid) {
      setStatus({ type: 'error', message: 'Please fix the errors in the form' });
      return;
    }

    try {
      setLoading(true);
      setStatus({ type: null, message: '' });

      // Initialize Firebase Storage
      const storage = getStorage();
      
      // Upload electricity bill if provided
      let electricityBillUrl = '';
      if (formData.electricityBill) {
        const file = formData.electricityBill;
        const storageRef = ref(storage, `electricity-bills/${file.name}`);
        
        try {
          // Upload the file
          await uploadBytes(storageRef, file);
          console.log('File uploaded successfully');
          
          // Get the download URL
          electricityBillUrl = await getDownloadURL(storageRef);
          console.log('Got download URL:', electricityBillUrl);
        } catch (uploadError) {
          console.error('Error uploading file:', uploadError);
          throw new Error('Failed to upload file');
        }
      }

      // Create the inquiry data with the download URL if available
      const inquiryData = {
        ...formData,
        timestamp: serverTimestamp(),
        electricityBillUrl: electricityBillUrl || null
      };

      // Remove the File object and undefined values from the data before submitting to Firestore
      const dataToSubmit = Object.entries(inquiryData).reduce((acc, [key, value]) => {
        if (value !== undefined && key !== 'electricityBill') {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      // Submit to Firestore
      const inquiriesRef = collection(db, 'inquiries');
      try {
        const docRef = await addDoc(inquiriesRef, {
          ...dataToSubmit,
          timestamp: serverTimestamp()
        });
        console.log('Document written with ID:', docRef.id);
      } catch (firestoreError) {
        console.error('Error writing to Firestore:', firestoreError);
        throw new Error('Failed to write to Firestore');
      }
      
      // Reset form and show success modal
      setFormData({
        fullName: '',
        email: '',
        mobile: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pinCode: '',
        sanctionLoad: '',
        availableArea: '',
        electricityAccountNumber: '',
        electricityBill: null
      });
      
      setShowSuccessModal(true);
      
      // Automatically redirect to home page after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/');
      }, 3000);
    } catch (error: unknown) {
      console.error('Error in handleSubmit:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to submit inquiry. Please try again.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Background Blur Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-gray-900/20 to-purple-900/20 backdrop-blur-[100px] pointer-events-none"></div>
      
      {/* Content */}
      <div className="relative container mx-auto px-4 py-12">
        <div className="mb-12 sm:mb-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 sm:mb-12">Submit Your Inquiry</h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6 bg-gray-900/50 backdrop-blur-sm rounded-2xl px-8 py-6 sm:px-12 sm:py-8 border border-white/5 shadow-xl shadow-blue-500/5 w-full mx-auto max-w-6xl"
            >
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                <div>
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Enter your full name"
                    />
                  </motion.div>
                </div>
                <div>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setFormData(prev => ({ ...prev, mobile: value }));
                        }
                      }}
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                      maxLength={10}
                      placeholder="Enter your 10-digit mobile number"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-6">
                <div>
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <input
                      type="text"
                      id="addressLine1"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Address line 1"
                    />
                  </motion.div>
                </div>
                <div>
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <input
                      type="text"
                      id="addressLine2"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Address line 2 (optional)"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Location Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Enter your city"
                    />
                  </motion.div>
                </div>
                <div>
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Enter your state"
                    />
                  </motion.div>
                </div>
                <div>
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <input
                      type="text"
                      id="pinCode"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Pin code"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Project Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div 
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <div className="relative">
                    <input
                      type="text"
                      id="sanctionLoad"
                      name="sanctionLoad"
                      value={formData.sanctionLoad}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Sanction load"
                      required
                    />
                  </div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <div className="relative">
                    <input
                      type="text"
                      id="availableArea"
                      name="availableArea"
                      value={formData.availableArea}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                      placeholder="Available area"
                      required
                    />
                  </div>
                </motion.div>
              </div>

              {/* Electricity Information */}
              <motion.div 
                className="grid grid-cols-1 gap-4"
                whileHover={{ scale: 1.01 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              >
                <div>
                  <input
                    type="text"
                    id="electricityAccountNumber"
                    name="electricityAccountNumber"
                    value={formData.electricityAccountNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base hover:border-blue-500/30"
                    placeholder="Electricity account number (optional)"
                  />
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <motion.div 
                  className="relative"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <input
                    type="file"
                    id="electricityBill"
                    name="electricityBill"
                    onChange={handleFileChange}
                    className="block w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500/20 file:text-blue-400 hover:file:bg-blue-500/30 cursor-pointer"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {formData.electricityBill && (
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-300 truncate max-w-[70%]">
                      {formData.electricityBill.name}
                    </span>
                  )}
                </motion.div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={!loading ? { scale: 1.02 } : {}}
                  whileTap={!loading ? { scale: 0.98 } : {}}
                  className={`w-full flex justify-center items-center px-6 py-3.5 rounded-xl text-base font-medium text-white ${
                    loading
                      ? 'bg-blue-500/80 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500/50'
                  } transition-all duration-200 shadow-lg hover:shadow-xl`}
                >
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={loading ? 'loading' : 'submit'}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {loading ? 'Submitting...' : 'Submit Inquiry'}
                    </motion.span>
                  </AnimatePresence>
                </motion.button>
              </div>
              {status.type && (
                <div className={`mt-4 p-4 rounded-lg ${
                  status.type === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'
                }`}>
                  <p className={`${
                    status.type === 'success' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {status.message}
                  </p>
                </div>
              )}
            </motion.form>
          </motion.div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-lg p-8 text-center max-w-md w-full"
          >
            <h3 className="text-2xl font-bold text-green-500 mb-4">Success!</h3>
            <p className="text-gray-300 mb-6">Your inquiry has been submitted successfully!</p>
            <p className="text-gray-400">Redirecting to home page in 3 seconds...</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Inquiry;